/**
 * The one clamp the encoder settings go through, whichever encoder they are for.
 *
 * Two groups of quality knobs are set on the admin page, travel to this device
 * with the session and are spent here: how a кружочек is recorded (capture.ts)
 * and how a picked video is re-encoded before it is sent (videoCompress.ts).
 * Both are clamped on the server on the way in and again here on the way out,
 * for the same reason — these are the numbers an encoder is configured with, and
 * the middle of a take is not where a stored setting should turn out to have
 * been nonsense.
 *
 * One table read by both, rather than the same loop written twice, because the
 * property that matters is that a device and the admin page never disagree about
 * what is set — and two copies of a rounding rule is exactly how they would come
 * to.
 */

export interface Limit {
  min: number;
  max: number;
  /**
   * What the value is snapped to. Whole numbers for anything measured in pixels,
   * frames or kilobits; a multiplier is the one kind of knob here that is
   * meaningfully fractional.
   */
  step?: number;
}

/**
 * `current` with whatever `next` says that is usable, each value snapped and
 * held inside its limit.
 *
 * A field that is missing or is not a finite number leaves the current one
 * alone, so a server older than a knob still delivers the ones it knows about
 * instead of resetting the rest to nothing.
 */
export function clampTunables<T>(current: T, next: Partial<T>, limits: Record<keyof T, Limit>): T {
  const applied = { ...current } as Record<string, number>;
  // Defended rather than typed: a server older than a whole group of knobs sends
  // no object at all, and a session that cannot be applied must not be a session
  // that cannot start.
  const incoming = (next ?? {}) as Record<string, unknown>;

  for (const [key, limit] of Object.entries(limits) as [string, Limit][]) {
    const value = incoming[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    // Through toFixed rather than left as the product: 2.5 / 0.1 is
    // 25.000000000000004 in binary floating point, and multiplying back gives a
    // number that renders with a tail of zeroes and a four.
    const grain = limit.step ?? 1;
    const snapped = Number((Math.round(value / grain) * grain).toFixed(4));
    applied[key] = Math.min(limit.max, Math.max(limit.min, snapped));
  }

  return applied as T;
}
